import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { normalizeRef, persistRef } from "../lib/referrals";
import { trackReferralVisit } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * On first mount, read ?ref= from the URL. If it matches a valid code:
 *   1. Persist to localStorage (30-day attribution window)
 *   2. Strip the ?ref= param from the address bar (cleaner shareable URL)
 *   3. Record a visit on the backend (once per browser session — dedupe via sessionStorage)
 *   4. Fire GA4 `referral_visit` custom event
 *
 * Safe to call anywhere in the tree — runs exactly once per full page load.
 */
export const useReferralCapture = () => {
    const location = useLocation();
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        const params = new URLSearchParams(location.search);
        const raw = params.get("ref");
        if (!raw) return;

        const code = normalizeRef(raw);
        if (!code) return;

        // 1. Persist
        persistRef(code);

        // 2. Clean the URL (remove ?ref=... without a navigation)
        params.delete("ref");
        const cleaned = params.toString();
        const url = window.location.pathname + (cleaned ? `?${cleaned}` : "") + window.location.hash;
        window.history.replaceState({}, "", url);

        // 3. Record backend visit — once per browser session
        const sessKey = `qgl_ref_visited_${code}`;
        if (!sessionStorage.getItem(sessKey)) {
            axios
                .post(`${API}/referral/visit`, { code })
                .then(() => sessionStorage.setItem(sessKey, "1"))
                .catch(() => { /* silent — never break UX on analytics */ });
        }

        // 4. GA4
        trackReferralVisit(code);
    }, []);
};

export default useReferralCapture;
